import React, { useState, useEffect, useMemo } from 'react';
import { toLocalYMD } from '../lib/dates';
import {
  Clock, ChevronLeft, ChevronRight, Loader2, User, Calendar,
  MapPin, CheckCircle2, Timer, TrendingUp, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Format time from ISO string
const formatTime = (isoString) => {
  if (!isoString) return '-';
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '-';
  }
};

// Format duration in minutes to readable string
const formatDuration = (minutes) => {
  if (!minutes && minutes !== 0) return '-';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

// Get Monday of the week containing the given date
const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

// Job row component
const JobRow = ({ job }) => {
  const hasTimeData = job.arrived_at || job.departed_at || job.time_on_site_minutes;
  const isCompleted = job.status === 'completed' || job.status === 'done';
  
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${
      isCompleted ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-zinc-200'
    }`}>
      {/* Status indicator */}
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
        isCompleted ? 'bg-emerald-500' : 'bg-zinc-300'
      }`} />
      
      {/* Customer & Service */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-zinc-900 truncate">{job.customer_name}</p>
        <p className="text-xs text-zinc-500 truncate">{job.service} • {job.address}</p>
      </div>
      
      {/* Scheduled time */}
      <div className="text-center w-16">
        <p className="text-xs text-zinc-500">Sched.</p>
        <p className="text-sm font-medium">{job.scheduled_time || '-'}</p>
      </div>
      
      {/* Arrived */}
      <div className="text-center w-16">
        <p className="text-xs text-zinc-500">Arrived</p>
        <p className={`text-sm font-medium ${job.arrived_at ? 'text-blue-600' : 'text-zinc-400'}`}>
          {formatTime(job.arrived_at)}
        </p>
      </div>
      
      {/* Left */}
      <div className="text-center w-16">
        <p className="text-xs text-zinc-500">Left</p>
        <p className={`text-sm font-medium ${job.departed_at ? 'text-orange-600' : 'text-zinc-400'}`}>
          {formatTime(job.departed_at)}
        </p>
      </div>
      
      {/* Time on site */}
      <div className="text-center w-20">
        <p className="text-xs text-zinc-500">On Site</p>
        <p className={`text-sm font-semibold ${job.time_on_site_minutes ? 'text-emerald-600' : 'text-zinc-400'}`}>
          {formatDuration(job.time_on_site_minutes)}
        </p>
      </div>
      
      {/* Price */}
      <div className="text-right w-16">
        <p className="text-sm font-semibold text-zinc-900">
          £{(job.price || 0).toFixed(2)}
        </p>
      </div>
    </div>
  );
};

// Day column component
const DayColumn = ({ dayName, date, jobs }) => {
  const isToday = new Date().toDateString() === date.toDateString();
  const totalJobs = jobs.length;
  const completedJobs = jobs.filter(j => j.status === 'completed' || j.status === 'done').length;
  const totalTimeMinutes = jobs.reduce((sum, j) => sum + (j.time_on_site_minutes || 0), 0);
  const totalValue = jobs.reduce((sum, j) => sum + (j.price || 0), 0);
  
  return (
    <div className={`bg-white rounded-xl border ${isToday ? 'border-emerald-400 ring-2 ring-emerald-100' : 'border-zinc-200'}`}>
      {/* Day header */}
      <div className={`px-4 py-3 border-b ${isToday ? 'bg-emerald-50 border-emerald-200' : 'bg-zinc-50 border-zinc-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`font-semibold ${isToday ? 'text-emerald-700' : 'text-zinc-800'}`}>{dayName}</h3>
            <p className="text-xs text-zinc-500">
              {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-zinc-900">{completedJobs}/{totalJobs}</p>
            <p className="text-xs text-zinc-500">{formatDuration(totalTimeMinutes)}</p>
          </div>
        </div>
      </div>
      
      {/* Jobs list */}
      <div className="p-3 space-y-2 max-h-96 overflow-y-auto">
        {jobs.length === 0 ? (
          <p className="text-sm text-zinc-400 text-center py-4">No jobs</p>
        ) : (
          jobs.map(job => (
            <JobRow key={job.id} job={job} />
          ))
        )}
      </div>
      
      {/* Day footer */}
      {jobs.length > 0 && (
        <div className="px-4 py-2 border-t border-zinc-100 bg-zinc-50/50">
          <p className="text-sm font-medium text-zinc-700 text-right">
            Total: £{totalValue.toFixed(2)}
          </p>
        </div>
      )}
    </div>
  );
};

const TimeCompletedReport = ({ onBack }) => {
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState('all');
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Fetch workers list
  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const response = await fetch(`${API_URL}/api/reports/time/workers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setWorkers(data.workers || []);
        }
      } catch (err) {
        console.error('Error fetching workers:', err);
      }
    };
    
    fetchWorkers();
  }, []);
  
  // Fetch report data
  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('streakfree_token');
        const weekStartStr = toLocalYMD(weekStart);
        
        let url = `${API_URL}/api/reports/time/weekly?week_start=${weekStartStr}`;
        if (selectedWorker !== 'all') {
          url += `&worker_id=${selectedWorker}`;
        }
        
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setReportData(data);
        } else {
          toast.error('Failed to load report');
        }
      } catch (err) {
        console.error('Error fetching report:', err);
        toast.error('Error loading report');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReport();
  }, [weekStart, selectedWorker]);
  
  // Navigate weeks
  const prevWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() - 7);
    setWeekStart(newStart);
  };
  
  const nextWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + 7);
    setWeekStart(newStart);
  };
  
  const goToThisWeek = () => {
    setWeekStart(getMonday(new Date()));
  };
  
  // Calculate week dates
  const weekDates = useMemo(() => {
    return DAY_KEYS.map((_, i) => {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      return date;
    });
  }, [weekStart]);
  
  // Format week range
  const weekRange = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    return `${weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }, [weekStart]);
  
  return (
    <div className="h-full flex flex-col bg-zinc-50" data-testid="time-completed-report">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ChevronLeft size={20} className="text-zinc-600" />
              </button>
            )}
            <div className="p-2 bg-purple-100 rounded-xl">
              <Clock size={24} className="text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-zinc-900">Time Completed Report</h1>
              <p className="text-sm text-zinc-500">Track arrival, departure & time on site</p>
            </div>
          </div>
        </div>
        
        {/* Filters row */}
        <div className="flex flex-wrap items-center gap-3 mt-4">
          {/* Week navigation */}
          <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
            <button
              onClick={prevWeek}
              className="p-2 hover:bg-zinc-200 rounded-md transition-colors"
              data-testid="prev-week"
            >
              <ChevronLeft size={16} className="text-zinc-600" />
            </button>
            <button 
              onClick={goToThisWeek}
              className="px-3 py-1.5 text-sm font-medium text-zinc-800 hover:bg-zinc-200 rounded-md transition-colors"
            >
              {weekRange}
            </button>
            <button
              onClick={nextWeek}
              className="p-2 hover:bg-zinc-200 rounded-md transition-colors"
              data-testid="next-week"
            >
              <ChevronRight size={16} className="text-zinc-600" />
            </button>
          </div>
          
          {/* Worker filter */}
          <select
            value={selectedWorker}
            onChange={(e) => setSelectedWorker(e.target.value)}
            className="px-4 py-2 border border-zinc-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            data-testid="worker-filter"
          >
            <option value="all">All Workers</option>
            {workers.map(worker => (
              <option key={worker.id} value={worker.id}>{worker.name}</option>
            ))}
          </select>
          
          {/* Summary stats */}
          {reportData && (
            <div className="flex items-center gap-4 ml-auto text-sm">
              <div className="flex items-center gap-1.5 text-zinc-600">
                <Calendar size={14} />
                <span>{reportData.summary.total_jobs} jobs</span>
              </div>
              <div className="flex items-center gap-1.5 text-emerald-600">
                <CheckCircle2 size={14} />
                <span>{reportData.summary.completed_jobs} done</span>
              </div>
              <div className="flex items-center gap-1.5 text-purple-600">
                <Timer size={14} />
                <span>{formatDuration(reportData.summary.total_time_on_site_minutes)} total</span>
              </div>
              <div className="flex items-center gap-1.5 text-blue-600">
                <TrendingUp size={14} />
                <span>~{formatDuration(reportData.summary.average_time_per_job_minutes)}/job</span>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 size={32} className="animate-spin text-purple-500" />
          </div>
        ) : reportData ? (
          <div className="space-y-4">
            {/* Weekly grid - 2 rows of days */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {DAY_KEYS.slice(0, 4).map((dayKey, i) => (
                <DayColumn
                  key={dayKey}
                  dayName={DAY_NAMES[i]}
                  date={weekDates[i]}
                  jobs={reportData.days[dayKey] || []}
                />
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {DAY_KEYS.slice(4).map((dayKey, i) => (
                <DayColumn
                  key={dayKey}
                  dayName={DAY_NAMES[i + 4]}
                  date={weekDates[i + 4]}
                  jobs={reportData.days[dayKey] || []}
                />
              ))}
            </div>
            
            {/* Info note */}
            <div className="flex items-start gap-2 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <AlertCircle size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">How time tracking works</p>
                <p className="mt-1">
                  When workers share their location, arrival and departure times are automatically recorded 
                  when they enter or leave a customer's address (50m radius). Time on site is calculated 
                  from the difference.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-zinc-500">
            <Clock size={48} className="text-zinc-300 mb-4" />
            <p>No data available for this period</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeCompletedReport;
